import { ZoneShape } from "../types";
import { getWallControlPoint, quadraticBezierPoint } from "./bezier";

export interface ZonePolygonPoint {
  x: number;
  y: number;
}

const CURVE_SAMPLES = 20;

/**
 * Flatten a zone polygon outline into straight-line points (same relative
 * coordinate space as the vertices). Curved edges are sampled along their
 * quadratic bezier — the same scheme wall-paths use — so fills and hit
 * areas follow the actual curve.
 */
export const sampleZonePolygonPoints = (
  vertices: ZonePolygonPoint[],
  edges: ZoneShape["edges"]
): ZonePolygonPoint[] => {
  const points: ZonePolygonPoint[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const from = vertices[i];
    const to = vertices[(i + 1) % vertices.length];
    const edge = edges?.[i] || {};
    const curvature = edge.curvature || 0;
    if (curvature === 0) {
      points.push(from);
      continue;
    }
    const { cx, cy } = getWallControlPoint(
      from.x,
      from.y,
      to.x,
      to.y,
      curvature,
      edge.curvatureCenter ?? 0.5
    );
    for (let s = 0; s < CURVE_SAMPLES; s++) {
      const t = s / CURVE_SAMPLES;
      points.push(quadraticBezierPoint(from.x, from.y, cx, cy, to.x, to.y, t));
    }
  }
  return points;
};

/**
 * Bounding box of the sampled outline (curves included), in the vertices'
 * own coordinate space.
 */
export const computeZonePolygonBounds = (
  vertices: ZonePolygonPoint[],
  edges: ZoneShape["edges"]
): { x: number; y: number; width: number; height: number } => {
  const points = sampleZonePolygonPoints(vertices, edges);
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

/**
 * Zone area in square pixels — shoelace formula on the sampled outline
 * (curves included) for polygon zones, `width * height` for the plain
 * rectangle. Divide by `pixelsPerMeter²` for real-world square meters.
 */
export const computeZoneAreaPx = (
  zone: Pick<ZoneShape, "width" | "height" | "vertices" | "edges">
): number => {
  if ((zone.vertices?.length ?? 0) < 3) {
    return zone.width * zone.height;
  }
  const points = sampleZonePolygonPoints(
    zone.vertices as ZonePolygonPoint[],
    zone.edges
  );
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    sum += current.x * next.y - next.x * current.y;
  }
  return Math.abs(sum) / 2;
};

/**
 * Re-anchor a zone so its origin (x/y) is the polygon's bbox top-left —
 * the invariant the rest of the editor relies on for selection bounds.
 * Returns the shape patch to apply after any vertex/edge edit.
 */
export const normalizeZonePolygon = (
  zone: Pick<ZoneShape, "x" | "y">,
  vertices: ZonePolygonPoint[],
  edges: ZoneShape["edges"]
): Partial<ZoneShape> => {
  const bounds = computeZonePolygonBounds(vertices, edges);
  return {
    x: Math.round(zone.x + bounds.x),
    y: Math.round(zone.y + bounds.y),
    width: Math.round(bounds.width),
    height: Math.round(bounds.height),
    vertices: vertices.map((vertex) => ({
      x: Math.round(vertex.x - bounds.x),
      y: Math.round(vertex.y - bounds.y)
    })),
    edges
  };
};
